import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Star, Clock, Users } from "lucide-react";
import { Link } from "react-router-dom";
import heroPattern from "@/assets/hero-pattern-opt.webp";

// Preload hero image for faster LCP
const preloadHeroImage = () => {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = heroPattern;
  link.type = "image/webp";
  document.head.appendChild(link);
};
preloadHeroImage();
const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(${heroPattern})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
      
      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-10 w-72 h-72 bg-gold/20 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />

      <div className="container relative z-10 mx-auto px-4 text-center">
        {/* Social proof top bar */}
        <div className="inline-flex items-center gap-2 bg-secondary/15 border border-secondary/30 text-secondary-foreground px-4 py-2 rounded-full mb-6 animate-fade-up">
          <Users className="w-4 h-4 text-secondary" />
          <span className="text-sm font-medium">+1200 شخص اكتشف مكمله الطبيعي هذا الشهر</span>
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3 h-3 fill-secondary text-secondary" />
            ))}
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-8 animate-fade-up">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">تركيبة طبيعية مخصصة لك</span>
        </div>

        {/* Main Heading */}
        <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <span className="text-gradient-gold">سعدتك</span>
        </h1>
        
        {/* Slogan */}
        <p className="text-2xl md:text-4xl font-display text-primary mb-4 animate-fade-up" style={{ animationDelay: '0.2s' }}>
          صحتك في تركيبتك
        </p>

        {/* Description */}
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
          اكتشف المكملات الغذائية الطبيعية المثالية لجسمك من خلال استبياننا الشخصي المبني على منتجات DXN عالية الجودة
        </p>

        {/* Time indicator */}
        <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mb-8 animate-fade-up" style={{ animationDelay: '0.35s' }}>
          <Clock className="w-4 h-4" />
          <span>3 دقائق فقط · مجاني تمامًا · نتائج فورية</span>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: '0.4s' }}>
          <Link to="/questionnaire">
            <Button variant="hero" size="xl" className="text-lg shadow-lg hover:shadow-xl">
              ابدأ الاستبيان المجاني الآن
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <a href="#products">
            <Button variant="outline" size="xl">
              تعرف على المنتجات
            </Button>
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8 animate-fade-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-xs font-bold text-primary">✓</span>
            </div>
            <span className="text-sm">شهادة حلال</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-xs font-bold text-primary">✓</span>
            </div>
            <span className="text-sm">جودة عالمية</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-xs font-bold text-primary">✓</span>
            </div>
            <span className="text-sm">منتجات طبيعية 100%</span>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA - visible only on mobile when scrolling */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-background/95 backdrop-blur-md border-t border-border px-4 py-3 animate-fade-up">
        <Link to="/questionnaire" className="block">
          <Button variant="hero" size="default" className="w-full text-sm font-semibold">
            ابدأ الاستبيان المجاني · 3 دقائق فقط
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default Hero;
